import {
  Flex,
  Table,
  Progress,
  Icon,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Switch,
  IconButton,
  Button,
  Skeleton,
  Tooltip,
  Badge,
  Box,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import androidImage from "assets/img/platform/android-alt.png";
import iosImage from "assets/img/platform/ios.png";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

import Card from "components/card/Card";

import { EditIcon } from "@chakra-ui/icons";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useHistory } from "react-router-dom";
import { Campaign } from "../../default/reducer";
import { useDispatch, useSelector } from "react-redux";
import {
  UpdateCampaignStatusAction,
  UpdateCampaignAfterSuggestionApplied,
} from "../../default/action";
import { NumberWithCommas } from "../../../../utils";
import { RootReducer } from "../../../../RootReducer";
import SuggestedCampaignActionsCell from "components/table/cell/suggested-campaign-actions";
import { IoSparkles } from "react-icons/io5";
import { Post } from "api";
import {
  hideGlobalLoading,
  showGlobalLoading,
} from "helper/GlobalLoadingDispatches";

import {
  AllSuggestionsModal,
  SuggestionsPreview,
  collectAllSuggestions,
} from "components/suggestions";

export default function ColumnsTable(props: {
  columnsData: any;
  tableData: Campaign[];
  isLoading: boolean;
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { columnsData, tableData, isLoading } = props;

  const allSuggestions = useMemo(
    () => collectAllSuggestions(tableData),
    [tableData]
  );
  const suggestionCount = useMemo(
    () => allSuggestions.length,
    [allSuggestions]
  );

  const handleSuggestionsApplied = useCallback(
    (appliedIds: number[]) => {
      allSuggestions
        .filter((suggestion) => appliedIds.includes(suggestion.id))
        .forEach((suggestion) => {
          const updatedValues: { bid?: number; dailyBudgetCap?: number } = {};

          if (suggestion.type === "bid") {
            updatedValues.bid = suggestion.newValue;
          } else if (suggestion.type === "daily_cap") {
            updatedValues.dailyBudgetCap = suggestion.newValue;
          }

          dispatch(
            UpdateCampaignAfterSuggestionApplied(
              suggestion.campaign.id,
              updatedValues
            )
          );
        });
    },
    [allSuggestions, dispatch]
  );

  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsHovered(true);
    }, 200);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  }, []);

  const tableInstance = useTable<Campaign>(
    {
      columns: columnsData,
      data: tableData,
      initialState: { pageSize: 9999 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const accountInfo = useSelector(
    (state: RootReducer) => state.authentication.accountInfo
  );
  const showPlatformSelectCached = localStorage.getItem("p") === "1";

  let showPlatformSelect = false;
  if (
    (accountInfo && accountInfo.showPlatformSelection) ||
    showPlatformSelectCached
  ) {
    showPlatformSelect = true;
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 9999;

  useEffect(() => {
    tableInstance.setPageSize(9999);
  }, [tableData, columnsData, isLoading, tableInstance]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const handleNavigateCampaignDetails = useCallback(
    (id: string, type: string) => {
      const url = `/ua/roas/create?id=${id}`;
      history.push(url);
    },
    [history]
  );

  const handleApplySuggestion = useCallback(
    async (suggestionId: number) => {
      try {
        showGlobalLoading("Applying suggestion...");

        await Post(`/dashboard/campaign-suggestions/${suggestionId}/apply`, {});

        const appliedSuggestion = allSuggestions.find(
          (s) => s.id === suggestionId
        );

        if (appliedSuggestion) {
          const updatedValues: { bid?: number; dailyBudgetCap?: number } = {};

          if (appliedSuggestion.type === "bid") {
            updatedValues.bid = appliedSuggestion.newValue;
          } else if (appliedSuggestion.type === "daily_cap") {
            updatedValues.dailyBudgetCap = appliedSuggestion.newValue;
          }

          dispatch(
            UpdateCampaignAfterSuggestionApplied(
              appliedSuggestion.campaign.id,
              updatedValues
            )
          );
        }
      } catch (error) {
        console.error("Error applying suggestion:", error);
      } finally {
        hideGlobalLoading();
      }
    },
    [allSuggestions, dispatch]
  );

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "scroll" }}
    >
      <Flex px="25px" justify="space-between" mb="10px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          ROAS Campaigns
        </Text>
        {suggestionCount > 0 && (
          <Box position="relative">
            <Button
              ref={buttonRef}
              leftIcon={<Icon as={IoSparkles} />}
              colorScheme="brand"
              variant="outline"
              size="sm"
              onClick={onOpen}
              //onMouseEnter={handleMouseEnter}
              //onMouseLeave={handleMouseLeave}
            >
              All Suggestions
              <Badge ml={2} colorScheme="brand" borderRadius="full">
                {suggestionCount}
              </Badge>
            </Button>

            {isHovered && (
              <Box
                position="absolute"
                top="0"
                right="calc(100% + 10px)"
                zIndex={9999}
                onMouseEnter={() => {
                  if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                  }
                  setIsHovered(true);
                }}
                onMouseLeave={handleMouseLeave}
                animation="tooltipFadeIn 0.15s ease-out"
                sx={{
                  "@keyframes tooltipFadeIn": {
                    "0%": {
                      opacity: 0,
                      transform: "translateX(10px)",
                    },
                    "100%": {
                      opacity: 1,
                      transform: "translateX(0)",
                    },
                  },
                }}
              >
                <SuggestionsPreview
                  suggestions={allSuggestions}
                  onApply={handleApplySuggestion}
                  onClose={() => setIsHovered(false)}
                  onOpenAllSuggestions={onOpen}
                />
              </Box>
            )}
          </Box>
        )}
      </Flex>

      <AllSuggestionsModal
        isOpen={isOpen}
        onClose={onClose}
        suggestions={allSuggestions}
        onApplySuccess={handleSuggestionsApplied}
      />

      <Table {...getTableProps()} variant="striped" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  px={3}
                  key={index}
                  borderColor={borderColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: "10px", lg: "12px" }}
                    color="gray.400"
                  >
                    {column.Header === "SUGGESTIONS" || column.Header === "ID"
                      ? ""
                      : column.render("Header")}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, index) => {
                  let data;
                  if (cell.column.Header === "NAME") {
                    data = (
                      <Flex
                        alignItems={"start"}
                        gap={1}
                        justifyContent="space-between"
                        px={1}
                      >
                        <Tooltip label={cell.value}>
                          <Text
                            _hover={{
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                            noOfLines={1}
                            onClick={() => {
                              handleNavigateCampaignDetails(
                                cell.row.original.id,
                                cell.row.original.biddingType
                              );
                            }}
                          >
                            {cell.value}
                          </Text>
                        </Tooltip>
                      </Flex>
                    );
                  } else if (cell.column.Header === "SUGGESTIONS") {
                    data = (
                      <Flex
                        alignItems={"start"}
                        gap={1}
                        justifyContent="space-between"
                        px={0}
                      >
                        {cell.row.original.biddingType !== "cpa" && (
                          <SuggestedCampaignActionsCell
                            campaign={cell.row.original}
                          />
                        )}
                      </Flex>
                    );
                  } else if (cell.column.Header === "ACTIVE") {
                    data = cell.row.original.isDirect ? (
                      <Switch
                        onChange={() => {
                          dispatch(
                            UpdateCampaignStatusAction(
                              //@ts-ignore
                              cell.row.original.id,
                              !cell.value
                            )
                          );
                        }}
                        style={{ marginLeft: 10 }}
                        isChecked={cell.value}
                        size="sm"
                      />
                    ) : null;
                  } else if (cell.column.Header === "APP") {
                    data = (
                      <Flex
                        style={{
                          width: 40,
                          height: 40,
                          maxWidth: 40,
                          maxHeight: 40,
                          borderRadius: 4,
                        }}
                      >
                        <img
                          referrerPolicy="no-referrer"
                          style={{
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: 4,
                          }}
                          src={cell.value}
                        />
                        {showPlatformSelect && (
                          <img
                            referrerPolicy="no-referrer"
                            style={{
                              width: 18,
                              height: 18,
                              minWidth: 18,
                              marginTop: 25,
                              marginLeft: 25,
                              borderRadius: 4,
                              filter: "drop-shadow(0.6px 0.6px 0.6px grey)",
                              position: "absolute",
                            }}
                            src={
                              // @ts-ignore
                              cell.row.original!.platform! == "ios"
                                ? iosImage
                                : androidImage
                            }
                          />
                        )}
                      </Flex>
                    );
                  } else if (cell.column.Header === "PROGRESS") {
                    data = (
                      <Flex align="center">
                        <Progress
                          variant="table"
                          colorScheme="brandScheme"
                          h="8px"
                          w="108px"
                          value={cell.value}
                        />
                      </Flex>
                    );
                  } else if (cell.column.Header === "CR") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {Math.round(cell.value * 100)}%
                      </Text>
                    );
                  } else if (cell.column.Header === "ID") {
                    data = (
                      <IconButton
                        aria-label={"edit"}
                        onClick={() => {
                          handleNavigateCampaignDetails(
                            cell.row.original.id,
                            cell.row.original.biddingType
                          );
                        }}
                        icon={<EditIcon />}
                      />
                    );
                  } else if (cell.column.Header === "GEOS") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {cell.value ? cell.value.join(", ") : "Global"}
                      </Text>
                    );
                  } else if (cell.column.Header === "DAILY CAP") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${Number(cell.value).toFixed(0)}
                      </Text>
                    );
                  } else if (cell.column.Header === "BID") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ROAS
                      </Text>
                    );
                  } else if (cell.column.Header === "SPEND") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${NumberWithCommas(Number(cell.value).toFixed(2))}
                      </Text>
                    );
                  } else if (cell.column.Header === "ECPI") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight={"500"}>
                        $
                        {NumberWithCommas(
                          // @ts-ignore
                          cell.row.original.installs
                            ? Number(
                                // @ts-ignore
                                cell.row.original.spend /
                                  // @ts-ignore
                                  cell.row.original.installs
                              ).toFixed(2)
                            : "0"
                        )}
                      </Text>
                    );
                  } else {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {cell.value}
                      </Text>
                    );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: "14px" }}
                      maxH="30px !important"
                      py="12px"
                      minW={{ sm: "150px", md: "200px", lg: "2px" }}
                      borderColor="transparent"
                      px={3}
                      maxW={300}
                    >
                      <Skeleton
                        borderRadius={16}
                        endColor={"rgba(53,63,255,0.17)"}
                        isLoaded={!isLoading}
                      >
                        {data}
                      </Skeleton>
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}
